import { Tables } from "@/lib/supabase/supabase_types_override";
import { supabase } from "@/lib/supabase/supabaseClient";
import { prepareQuestionsWithNewIdsAndMapping } from "@/lib/utils/replaceOldIdsWithNewIds";
import { merge } from "lodash";
import { UseSyncSurveysWithTemplateProps } from ".";
import { QuestionChangesAffectingResponsesArray, SurveyCheckResult } from "../../types/syncCheckResult";
import { checkSurveyForDangerousChanges } from "../useCheckSyncSurveysWithTemplate/lib/checkSurveyForDangerousChanges";
import { willChangeAffectResponses } from "../../lib/utils";

export const syncSurveysWithTemplate = async ({
  surveyTemplateId,
  selectedItemsForSync,
}: UseSyncSurveysWithTemplateProps) => {
  const surveyTemplateRes = await supabase
    .from("survey_bank")
    .select("*")
    .eq("id", surveyTemplateId)
    .single();
  if (surveyTemplateRes.error || !surveyTemplateRes.data) {
    throw surveyTemplateRes.error || new Error("Survey template not found");
  }
  const surveyTemplateQuestionsRes = await supabase
    .from("survey_bank_question_bank")
    .select("*")
    .eq("survey_bank_id", surveyTemplateId);
  if (surveyTemplateQuestionsRes.error) {
    throw surveyTemplateQuestionsRes.error;
  }
  const surveysToSyncRes = await supabase
    .from("survey")
    .select("*, crm(*)")
    .eq("survey_bank_ref_id", surveyTemplateId);
  if (surveysToSyncRes.error) {
    throw surveysToSyncRes.error;
  }
  const surveysForSync = selectedItemsForSync
    ? surveysToSyncRes.data.filter((survey) =>
        selectedItemsForSync?.hasOwnProperty(survey.id)
      )
    : surveysToSyncRes.data;
  console.group("SYNC SURVEYS WITH TEMPLATE");
  console.log("Survey Template ID:", surveyTemplateId);
  console.log("Selected Items for Sync:", selectedItemsForSync);
  const syncedSurveys: Tables<"survey">[] = [];
  try {
    for (const survey of surveysForSync) {
      const surveyDangerousChanges = checkSurveyForDangerousChanges(
        survey,
        surveyTemplateRes.data
      );
      const {
        archived,
        survey_ref_id,
        crm_system,
        created_at,
        author_id,
        id,
        ...syncData
      } = surveyTemplateRes.data;
      let surveyDataToSync = merge(
        {},
        selectedItemsForSync?.[survey.id]?.survey?.name
          ? { name: syncData.name }
          : {},
        selectedItemsForSync?.[survey.id]?.survey?.description
          ? { description: syncData.description }
          : {}
      );
      console.log(survey);
      console.log(surveyDataToSync);
      if (
        surveyDataToSync.hasOwnProperty("name") ||
        surveyDataToSync.hasOwnProperty("description")
      ) {
        const updatedSurveyRes = await supabase
          .from("survey")
          .update(surveyDataToSync)
          .eq("id", survey.id)
          .select("*")
          .maybeSingle();
        console.log("updatedSurveyRes");
        console.log(updatedSurveyRes);
        if (updatedSurveyRes.error || !updatedSurveyRes.data) {
          throw updatedSurveyRes.error || new Error("Failed to update survey");
        }
      }
      const templateQuestionsIDsToSync = selectedItemsForSync
        ? selectedItemsForSync[survey.id]?.questionsToSync
        : undefined;
      await syncQuestionsForSurvey(
        survey.id,
        surveyTemplateQuestionsRes.data,
        templateQuestionsIDsToSync,
        surveyDangerousChanges
      );
      await supabase
        .from("question")
        .delete()
        .in("id", selectedItemsForSync?.[survey.id]?.questionsToDelete || []);
      syncedSurveys.push(survey);
    }
  } catch (err) {
    console.error("Error syncing surveys with template:", err);
    for (const survey of syncedSurveys) {
      const originalSurveyBody = surveysToSyncRes.data.find(
        (s) => s.id === survey.id
      )!;
      const rollBackSurveyRes = await supabase
        .from("survey")
        .update(originalSurveyBody)
        .eq("id", survey.id);
      if (rollBackSurveyRes.error) {
        console.error("Error rolling back survey:", rollBackSurveyRes.error);
      }
    }
  }
  console.groupEnd();
};

async function syncQuestionsForSurvey(
  surveyId: Tables<"survey">["id"],
  templateQuestions: Tables<"survey_bank_question_bank">[],
  templateQuestionsIdsToSync?: Array<Tables<"survey_bank_question_bank">["id"]>,
  surveyCheckResult?: SurveyCheckResult
) {
  const surveyQuestionsRes = await supabase
    .from("question")
    .select("*")
    .eq("survey_id", surveyId);
  if (surveyQuestionsRes.error) {
    throw surveyQuestionsRes.error;
  }
  console.group("  SYNC QUESTIONS FOR SURVEY");
  console.log("  Survey ID:", surveyId);
  console.log("  Template Questions IDs to Sync:", templateQuestionsIdsToSync);
  try {
    const { questions: templateQuestionsWithNewIds, idMapping } =
      prepareQuestionsWithNewIdsAndMapping<Tables<"survey_bank_question_bank">>(
        {
          questions: templateQuestions,
        }
      );

    // Create reverse mapping (newId -> oldId) for lookups
    const reverseIdMapping = Object.fromEntries(
      Object.entries(idMapping).map(([oldId, newId]) => [newId, oldId])
    );

    // Map original template question IDs to new IDs for filtering
    const newTemplateQuestionIdsToSync = templateQuestionsIdsToSync
      ?.map((id) => idMapping[id])
      .filter(Boolean);

    const questionsToSync = newTemplateQuestionIdsToSync
      ? templateQuestionsWithNewIds.filter((q) =>
          newTemplateQuestionIdsToSync.includes(q.id)
        )
      : templateQuestionsWithNewIds;
    console.log("questionsToSync", questionsToSync);
    const questionsToAdd = templateQuestionsIdsToSync?.filter(
      (id) =>
        !surveyQuestionsRes.data.some(
          (q) => q.survey_bank_question_bank_ref_id === id
        )
    );
    console.log("questionsToAdd", questionsToAdd);
    for (const templateQuestion of questionsToSync) {
      console.group("    Syncing Question ID:", templateQuestion.id);
      const {
        created_at,
        survey_bank_id,
        organisation_id,
        crm_id,
        ...syncData
      } = templateQuestion;
      const updateQuestionsRes = await supabase
        .from("question")
        .update({
          ...syncData,
          survey_id: surveyId,
        })
        .eq(
          "survey_bank_question_bank_ref_id",
          templateQuestionsIdsToSync
            ? reverseIdMapping[templateQuestion.id]
            : undefined
        )
        .eq("survey_id", surveyId)
        .select("*");

      console.log("updateQuestionsRes", updateQuestionsRes);
      if (updateQuestionsRes.error || !updateQuestionsRes.data) {
        throw (
          updateQuestionsRes.error || new Error("Failed to update question")
        );
      }
      console.group("      Checking for response-affecting changes");
      const questionDangerousChanges = surveyCheckResult?.questionsToSync.find(
        (qc) => qc.templateQuestion?.id === templateQuestion.id
      )?.dangerousChanges;
      console.log("questionDangerousChanges", questionDangerousChanges);
      const willChangeAffectResponsesRes = willChangeAffectResponses(
        questionDangerousChanges || []
      );
      console.log("willChangeAffectResponsesRes", willChangeAffectResponsesRes);
      if (willChangeAffectResponsesRes) {
        const deleteResponsesRes = await supabase
          .from("response")
          .delete()
          .eq("question_id", updateQuestionsRes.data?.[0]?.id);
        console.log("deleteResponsesRes", deleteResponsesRes);
        if (deleteResponsesRes.error) {
          throw deleteResponsesRes.error;
        }
      }
      console.groupEnd();
      console.groupEnd();
    }
    for (const templateQuestionId of questionsToAdd || []) {
      console.group("    Adding Question ID:", templateQuestionId);
      // templateQuestionId is the original ID, need to find by new ID
      const templateQuestion = templateQuestionsWithNewIds.find(
        (q) => q.id === idMapping[templateQuestionId]
      );
      console.log("templateQuestion", templateQuestion);
      if (!templateQuestion) continue;
      const {
        created_at,
        survey_bank_id,
        organisation_id,
        crm_id,
        ...newQuestionData
      } = templateQuestion;
      const addQuestionRes = await supabase
        .from("question")
        .insert({
          ...newQuestionData,
          survey_bank_question_bank_ref_id: templateQuestionId,
          survey_id: surveyId,
        })
        .select("*")
        .single();
      console.log("addQuestionRes", addQuestionRes);
      if (addQuestionRes.error || !addQuestionRes.data) {
        console.groupEnd();
        throw addQuestionRes.error || new Error("Failed to add question");
      }
      console.groupEnd();
    }
  } catch (err) {
    console.error("Error syncing questions for survey:", err);
    await rollBackQuestions(
      surveyId,
      surveyQuestionsRes.data,
      templateQuestionsIdsToSync
    );
    throw err;
  }
}

async function rollBackQuestions(
  surveyId: Tables<"survey">["id"],
  originalQuestions: Tables<"question">[],
  syncedTemplateQuestionsIds?: Tables<"survey_bank_question_bank">["id"][]
) {
  const questionsToRollBack = syncedTemplateQuestionsIds
    ? originalQuestions
        .filter((q) => q?.survey_bank_question_bank_ref_id)
        .filter((q) =>
          syncedTemplateQuestionsIds.includes(
            q.survey_bank_question_bank_ref_id!
          )
        )
    : originalQuestions;
  const questionsToDelete = syncedTemplateQuestionsIds?.filter(
    (id) =>
      !originalQuestions.some((q) => q.survey_bank_question_bank_ref_id === id)
  );
  for (const question of questionsToRollBack) {
    if (!question.survey_bank_question_bank_ref_id) continue;
    const { survey_bank_question_bank_ref_id, ...updateData } = question;
    const rollBackRes = await supabase
      .from("question")
      .update(updateData)
      .eq(
        "survey_bank_question_bank_ref_id",
        question.survey_bank_question_bank_ref_id
      )
      .eq("survey_id", surveyId);
    if (rollBackRes.error) {
      console.error("Error rolling back question:", rollBackRes);
      throw Error("Error rolling back question");
    }
  }
  for (const questionId of questionsToDelete || []) {
    const deleteRes = await supabase
      .from("question")
      .delete()
      .eq("survey_bank_question_bank_ref_id", questionId)
      .eq("survey_id", surveyId);
    if (deleteRes.error) {
      console.error("Error deleting question:", deleteRes);
      throw Error("Error deleting question");
    }
  }
}
